import {
  ChangeDetectionStrategy,
  Component,
  input,
  InputSignal,
  OnInit, output,
  OutputEmitterRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
  selector: 'app-time-input',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatTooltipModule
  ],
  templateUrl: './time-input.component.html',
  styleUrl: './time-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimeInputComponent implements OnInit {
  control: InputSignal<FormControl<string | null>>
    = input.required<FormControl<string | null>>();

  label: InputSignal<string | null> = input<string | null>('');

  placeholder: InputSignal<string> = input<string>('HH:MM');

  options: InputSignal<string[]> = input<string[]>([]);

  isOptionDisabled: InputSignal<((opt: string) => boolean) | null>
    = input<((opt: string) => boolean) | null>(null);

  optionTooltip: InputSignal<((opt: string) => string | null) | null>
    = input<((opt: string) => string | null) | null>(null);


  readonly blurEvent: OutputEmitterRef<void> = output();

  filteredOptions$: Observable<string[]> = of([]);

  ngOnInit (): void {
    this.filteredOptions$ = this.control().valueChanges.pipe(
      startWith(this.control().value ?? ''),
      map(() => this.options())
    );
  }

  onInput (inputEvent: Event): void {
    const inputEl: HTMLInputElement = inputEvent.target as HTMLInputElement;
    const rawValue: string = inputEl.value.replaceAll(/[^\d]/g, '');
    const digits: string = rawValue.slice(-4);

    let hours: string;
    let minutes: string;

    if (!digits) {
      this.control().setValue('', { emitEvent: false });

      return;
    }

    if (digits.length <= 2) {
      hours = digits;
      minutes = '';
    } else {
      hours = digits.slice(0, -2);
      minutes = digits.slice(-2);
    }

    const formatted: string = `${ hours.padStart(2, '0') }:${ minutes.padEnd(2, '0') }`;

    this.control().setValue(formatted, { emitEvent: false });
  }

  onFocus (focusEvent: FocusEvent): void {
    const inputEl: HTMLInputElement = focusEvent.target as HTMLInputElement;

    if (!this.control().value)
      this.control().setValue('', { emitEvent: false });


    setTimeout(() => inputEl.select(), 0);
  }

  onKey (keyEvent: KeyboardEvent): void {
    const inputEl: HTMLInputElement = keyEvent.target as HTMLInputElement;
    const pressedKey: string = keyEvent.key;

    if (
      (keyEvent.ctrlKey || keyEvent.metaKey)
      && [ 'a', 'A', 'c', 'C', 'v', 'V', 'x', 'X' ].includes(pressedKey)
    )
      return;


    const navigationKeys: string[] = [ 'Tab', 'Enter', 'Escape', 'ArrowLeft', 'ArrowRight' ];

    if (navigationKeys.includes(pressedKey)) return;

    let currentValue: string = String((this.control().value as string | null | undefined) ?? '');

    const cursorPosition: number = inputEl.selectionStart ?? 0;

    let editPosition: number = cursorPosition;

    const filledValue: string = currentValue.padEnd(5, '-');
    const valueChars: string[] = [ ...filledValue ];

    valueChars[ 2 ] = ':';

    if (pressedKey === 'Backspace' || pressedKey === 'Delete') {
      keyEvent.preventDefault();

      const selStart: number = inputEl.selectionStart ?? 0;
      const selEnd: number = inputEl.selectionEnd ?? selStart;

      if (selEnd > selStart) {
        for (let i: number = selStart; i < selEnd; i++)
          if (i !== 2) valueChars[ i ] = '-';

        editPosition = selStart;
      } else {
        if (pressedKey === 'Backspace')
          editPosition = editPosition === 3 ? 1 : Math.max(0, editPosition - 1);


        if (editPosition === 2) editPosition--;

        if (editPosition >= 0 && editPosition < valueChars.length && valueChars[ editPosition ] !== ':')
          valueChars[ editPosition ] = '-';
      }

      currentValue = valueChars.join('');
      this.control().setValue(currentValue, { emitEvent: false });
      inputEl.value = currentValue;
      setTimeout(() => inputEl.setSelectionRange(editPosition, editPosition), 0);

      return;
    }

    if (pressedKey === ':') {
      keyEvent.preventDefault();

      return;
    }

    if (!(/^\d$/).test(pressedKey)) {
      keyEvent.preventDefault();

      return;
    }

    keyEvent.preventDefault();

    if (editPosition === 2) editPosition++;

    if (editPosition >= 5) return;

    valueChars[ editPosition ] = pressedKey;

    const newValue: string = valueChars.join('');

    this.control().setValue(newValue, { emitEvent: false });
    inputEl.value = newValue;

    let nextPos: number = editPosition + 1;

    if (nextPos === 2) nextPos++;

    if (nextPos > 5) nextPos = 5;

    setTimeout(() => inputEl.setSelectionRange(nextPos, nextPos), 0);
  }

  onBlur (): void {
    this.blurEvent.emit();
  }

  getTooltip (opt: string): string | null {
    const tooltipFn: ((opt: string) => string | null) | null = this.optionTooltip();

    if (!tooltipFn) return null;

    return tooltipFn(opt);
  }
}
